import { useTranslation } from 'react-i18next';

import {
    DealCheckboxCell,
    DealObjectCell,
    DealTableCell,
    DealNoteCell,
    useDealsTable,
} from '@crm/entities/deal';
import { LostDeal, PostponeDeal, ReturnDealsToPipeline } from '@crm/features';
import ArchiveIcon from '@crm/shared/assets/icons/archive.svg?react';
import CalendarCheckOutIcon from '@crm/shared/assets/icons/calendar-check-out.svg?react';
import LinkBackwardIcon from '@crm/shared/assets/icons/link-backward.svg?react';
import { cn, getPluralized, tw } from '@crm/shared/lib';
import {
    Button,
    SelectionActionsBar,
    SortableTableHead,
    SvgIcon,
    Table,
    TableBody,
    TableHead,
    TableHeader,
    TableRow,
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@crm/shared/ui';
import { formatPrice } from '@shared/helpers';

import { DealPaymentDataCell } from './DealPaymentDataCell';
import { DealProcessCell } from './DealProcessCell';
import { NextContactCell } from './NextContactCell';
import { DEALS_TABLE_SORT_FIELDS } from '../../model/consts';

import type {
    TInWorkOrFinishedDealsData,
    TInWorkOrFinishedDealsSortDirection,
    TInWorkOrFinishedDealsSortField,
} from '@crm/entities/deal';

interface DealsTableProps {
    allowClear: boolean;
    dealItems: TInWorkOrFinishedDealsData['items'];
    direction: TInWorkOrFinishedDealsSortDirection | null;
    onSortChange: (sortState: {
        direction: TInWorkOrFinishedDealsSortDirection | null;
        sort: TInWorkOrFinishedDealsSortField | null;
    }) => void;
    pipelineColumns: TInWorkOrFinishedDealsData['pipelineColumnsData']['columns'];
    sort: TInWorkOrFinishedDealsSortField | null;
}

export const DealsTable = ({
    allowClear,
    dealItems,
    direction,
    onSortChange,
    pipelineColumns,
    sort,
}: DealsTableProps) => {
    const { t } = useTranslation();
    // Trade-off: для каждой строки создаются экземпляры feature-компонентов с диалогами.
    // Если количество одновременно отрендеренных строк заметно вырастет, действия стоит
    // снова перевести на один управляемый диалог на уровне таблицы.
    const tableGridTemplateColumnsStyles = tw(
        'grid-cols-[24px_minmax(100px,1fr)_minmax(140px,max-content)_150px_minmax(100px,max-content)_minmax(100px,max-content)_minmax(220px,2fr)_100px]'
    );
    const {
        clearSelectedDeals,
        handleRowClick,
        handleRowKeyDown,
        handleSelectAllDeals,
        handleSelectDeal,
        isAllDealsSelected,
        isSomeDealsSelected,
        selectedDealIds,
    } = useDealsTable({ dealItems });
    const selectedDealIdsList = Array.from(selectedDealIds);

    return (
        <div className="relative h-full">
            <Table
                containerClassName="h-full overflow-y-auto"
                className={cn('grid min-w-250 gap-x-3', tableGridTemplateColumnsStyles)}
            >
                <TableHeader className="sticky top-0 z-10 col-span-full grid grid-cols-subgrid bg-white [&_th]:h-auto [&_th]:py-2 [&_th]:text-xs [&_th]:font-normal [&_th]:text-smtr-main-tertiary-100 [&_tr]:border-0">
                    <TableRow className="col-span-full grid grid-cols-subgrid items-center hover:bg-transparent">
                        <DealCheckboxCell
                            variant="header"
                            checked={isAllDealsSelected}
                            indeterminate={isSomeDealsSelected && !isAllDealsSelected}
                            aria-label={t('crm.common.dealsTable.selectAllAriaLabel')}
                            onCheckedChange={handleSelectAllDeals}
                        />
                        <TableHead className="px-1">
                            {t('crm.inWorkOrFinishedPage.table.header.object')}
                        </TableHead>
                        <TableHead className="px-1">
                            {t('crm.inWorkOrFinishedPage.table.header.progress')}
                        </TableHead>
                        <SortableTableHead
                            activeSort={sort}
                            align="center"
                            allowClear={allowClear}
                            direction={direction}
                            sortField={DEALS_TABLE_SORT_FIELDS.nextContactAt}
                            onSortChange={onSortChange}
                        >
                            {t('crm.inWorkOrFinishedPage.table.header.nextContact')}
                        </SortableTableHead>
                        <SortableTableHead
                            activeSort={sort}
                            allowClear={allowClear}
                            direction={direction}
                            sortField={DEALS_TABLE_SORT_FIELDS.paymentAt}
                            onSortChange={onSortChange}
                        >
                            {t('crm.inWorkOrFinishedPage.table.header.payment')}
                        </SortableTableHead>
                        <TableHead className="px-1 text-center">
                            {t('crm.inWorkOrFinishedPage.table.header.paid')}
                        </TableHead>
                        <TableHead className="px-1">
                            {t('crm.inWorkOrFinishedPage.table.header.note')}
                        </TableHead>
                        <TableHead className="px-1 text-right">
                            {t('crm.inWorkOrFinishedPage.table.header.actions')}
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody className="col-span-full grid grid-cols-subgrid gap-y-1 [&_td]:py-2">
                    {dealItems.map((deal) => (
                        <TableRow
                            key={deal.id}
                            className="col-span-full grid h-12.5 cursor-pointer grid-cols-subgrid items-center rounded-md border-0 hover:bg-smtr-main-bgGray-50 data-[state=selected]:bg-smtr-main-accent-50"
                            data-state={selectedDealIds.has(deal.id) ? 'selected' : undefined}
                            tabIndex={0}
                            aria-label={t('crm.common.dealsTable.row.openAriaLabel', {
                                dealName: deal.objectName ?? deal.id,
                            })}
                            onClick={(event) => handleRowClick(event, deal.id)}
                            onKeyDown={(event) => handleRowKeyDown(event, deal.id)}
                        >
                            <DealCheckboxCell
                                checked={selectedDealIds.has(deal.id)}
                                aria-label={t('crm.common.dealsTable.row.selectAriaLabel', {
                                    dealName: deal.objectName ?? deal.id,
                                })}
                                onCheckedChange={(checked) => handleSelectDeal(deal.id, checked)}
                            />
                            <DealObjectCell
                                objectName={deal.objectName}
                                className="max-w-100"
                                maxLines={2}
                            />
                            <DealProcessCell
                                currency={deal.progress.currency}
                                earnedAmount={deal.progress.earnedAmount}
                                percent={deal.progress.percent}
                            />
                            <NextContactCell dealId={deal.id} nextContactAt={deal.nextContactAt} />
                            <DealPaymentDataCell
                                amount={deal.payment.amount}
                                currency={deal.payment.currency}
                                date={deal.payment.date}
                            />
                            <DealTableCell
                                className="px-1 text-center whitespace-nowrap"
                                isEmpty={deal.paid.paidAmount === null}
                                fallback="-"
                            >
                                {deal.paid.paidAmount !== null &&
                                    formatPrice(deal.paid.paidAmount, {
                                        currencyCode: deal.paid.currency,
                                        withCurrency: true,
                                    })}
                            </DealTableCell>
                            {/* <CommentCell dealId={deal.id} comment={deal.requestEssence} /> */}
                            <DealNoteCell textContent={deal.comment ?? '-'} />
                            <DealTableCell className="px-1">
                                <div className="flex items-center justify-end gap-1">
                                    <Tooltip>
                                        <ReturnDealsToPipeline
                                            dealIds={[deal.id]}
                                            columns={pipelineColumns}
                                            trigger={
                                                <TooltipTrigger
                                                    render={
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon-sm"
                                                            className="size-6 rounded-[6px] bg-blue-50 text-blue-500 hover:bg-blue-100 hover:text-blue-500"
                                                            aria-label={t(
                                                                'crm.common.actions.returnToPipeline'
                                                            )}
                                                        >
                                                            <SvgIcon
                                                                icon={LinkBackwardIcon}
                                                                size="16"
                                                            />
                                                        </Button>
                                                    }
                                                />
                                            }
                                        />
                                        <TooltipContent>
                                            {t('crm.common.actions.returnToPipeline')}
                                        </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <LostDeal
                                            dealIds={[deal.id]}
                                            invalidationScope="inWorkOrFinished"
                                            trigger={
                                                <TooltipTrigger
                                                    render={
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon-sm"
                                                            className="size-6 rounded-[6px] bg-smtr-border-100 text-smtr-main-tertiary-100"
                                                            aria-label={t(
                                                                'crm.common.actions.markLost'
                                                            )}
                                                        >
                                                            <SvgIcon icon={ArchiveIcon} size="16" />
                                                        </Button>
                                                    }
                                                />
                                            }
                                        />
                                        <TooltipContent>
                                            {t('crm.common.actions.markLost')}
                                        </TooltipContent>
                                    </Tooltip>
                                    <Tooltip>
                                        <PostponeDeal
                                            dealIds={[deal.id]}
                                            trigger={
                                                <TooltipTrigger
                                                    render={
                                                        <Button
                                                            type="button"
                                                            variant="ghost"
                                                            size="icon-sm"
                                                            className="size-6 rounded-[6px] bg-smtr-border-100 text-smtr-main-tertiary-100"
                                                            aria-label={t(
                                                                'crm.common.actions.postponeDeal'
                                                            )}
                                                        >
                                                            <SvgIcon
                                                                icon={CalendarCheckOutIcon}
                                                                size="16"
                                                                className="text-smtr-main-green-100"
                                                            />
                                                        </Button>
                                                    }
                                                />
                                            }
                                        />
                                        <TooltipContent>
                                            {t('crm.common.actions.postponeDeal')}
                                        </TooltipContent>
                                    </Tooltip>
                                </div>
                            </DealTableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {selectedDealIds.size > 0 && (
                <SelectionActionsBar
                    label={t('crm.common.dealsTable.selection.label', {
                        deals: getPluralized(selectedDealIds.size, {
                            one: t('crm.common.plurals.deal.one'),
                            few: t('crm.common.plurals.deal.few'),
                            many: t('crm.common.plurals.deal.many'),
                        }),
                    })}
                    onClose={clearSelectedDeals}
                >
                    <ReturnDealsToPipeline
                        dealIds={selectedDealIdsList}
                        columns={pipelineColumns}
                        trigger={
                            <Button type="button">
                                {t('crm.common.actions.returnToPipeline')}
                            </Button>
                        }
                    />
                    <LostDeal
                        dealIds={selectedDealIdsList}
                        invalidationScope="inWorkOrFinished"
                        onSuccess={clearSelectedDeals}
                        trigger={
                            <Button type="button" className="bg-gray-400 hover:bg-gray-600">
                                {t('crm.common.actions.markLost')}
                            </Button>
                        }
                    />
                    <PostponeDeal
                        dealIds={selectedDealIdsList}
                        onSubmit={clearSelectedDeals}
                        trigger={
                            <Button
                                type="button"
                                className="bg-smtr-main-green-100 hover:bg-smtr-main-green-200"
                            >
                                {t('crm.common.actions.postponeDeal')}
                            </Button>
                        }
                    />
                </SelectionActionsBar>
            )}
        </div>
    );
};
